import type { IModelInfo } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Badge, Button, Group, Paper, PasswordInput, Text } from "@mantine/core"
import { useCallback, useState } from "react"
import { useCompanyApi } from "../../../hooks/useCompanyApi"
import { testLLMProvider } from "../../../lib/api"
import { extractErrorMessage } from "../../../lib/api/errors"

interface IProps {
  name: string
  label: string
  currentKey: string
  /** A secret is stored server-side but masked — the client never holds it.
   *  Shows a "Configured" badge + placeholder and disables Test (you can't
   *  test a key you don't have). Cleared the moment the user types a new key. */
  configured?: boolean
  onKeyChange: (key: string) => void
  onSave: () => void
  saving: boolean
  onTestOverride?: (provider: string, key: string) => Promise<{ valid: boolean; models: IModelInfo[]; error?: string }>
}

export function ProviderCard({
  name,
  label,
  currentKey,
  configured = false,
  onKeyChange,
  onSave,
  saving,
  onTestOverride,
}: IProps): React.JSX.Element {
  const company = useCompanyApi()
  const { i18n } = useLingui()
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ valid: boolean; models: IModelInfo[]; error?: string } | null>(null)

  // A configured key with no freshly-typed replacement: the saved value is
  // unknown to the client, so Test is meaningless and Save is a no-op.
  const isConfiguredUnchanged = configured && currentKey.trim() === ""

  const handleTest = useCallback(async (): Promise<void> => {
    if (!currentKey.trim()) return
    setTesting(true)
    setTestResult(null)
    try {
      let result: { valid: boolean; models: IModelInfo[]; error?: string }
      if (onTestOverride) {
        result = await onTestOverride(name, currentKey)
      } else if (company) {
        result = await testLLMProvider(company, name, currentKey)
      } else {
        return
      }
      setTestResult(result)
    } catch (err) {
      setTestResult({ valid: false, models: [], error: extractErrorMessage(err) })
    } finally {
      setTesting(false)
    }
  }, [name, currentKey, company, onTestOverride])

  return (
    <Paper withBorder p="md">
      <Group justify="space-between" mb="sm">
        <Text fw={600}>{label}</Text>
        {testResult ? (
          <Badge color={testResult.valid ? "success" : "danger"} variant="dot">
            {testResult.valid ? i18n._(msg`Connected (${testResult.models.length} models)`) : i18n._(msg`Error`)}
          </Badge>
        ) : isConfiguredUnchanged ? (
          <Badge color="success" variant="light">
            <Trans>Configured</Trans>
          </Badge>
        ) : !currentKey ? (
          <Badge color="gray" variant="outline">
            <Trans>Not configured</Trans>
          </Badge>
        ) : null}
      </Group>
      <Group align="end" gap="sm">
        <PasswordInput
          label={i18n._(msg`API key`)}
          placeholder={
            isConfiguredUnchanged
              ? i18n._(msg`•••• saved key — enter a new one to replace`)
              : i18n._(msg`Enter ${label} API key`)
          }
          value={currentKey}
          onChange={(e) => onKeyChange(e.currentTarget.value)}
          style={{ flex: 1 }}
          size="sm"
        />
        <Button
          size="sm"
          variant="light"
          onClick={handleTest}
          loading={testing}
          disabled={!currentKey.trim() || (!company && !onTestOverride)}
        >
          <Trans>Test</Trans>
        </Button>
        <Button size="sm" onClick={onSave} loading={saving} disabled={!currentKey.trim()}>
          <Trans>Save</Trans>
        </Button>
      </Group>
      {testResult?.error && (
        <Alert color="danger" variant="light" mt="sm">
          {testResult.error}
        </Alert>
      )}
      {testResult?.valid && testResult.models.length > 0 && (
        <Text size="xs" c="dimmed" mt="sm">
          <Trans>Available:</Trans>{" "}
          {testResult.models
            .slice(0, 8)
            .map((m) => m.name || m.id)
            .join(", ")}
          {testResult.models.length > 8 ? i18n._(msg` +${testResult.models.length - 8} more`) : ""}
        </Text>
      )}
    </Paper>
  )
}
